import { Fragment, useEffect, useMemo, useState } from 'react'
import useCustomSchedule from '../../../../../hooks/useCustomSchedule'
import RightSection from '../../../planning/schedule/custom-schedule/RightSection'
import { GeneratingScheduleIcon, NoCommandSelectedIcon, NoSchedulePreview } from '../../../planning/Svgs'
import CustomScheduleFormBody from './CustomScheduleFormBody'
import CustomScheduleFormFooter from './CustomScheduleFormFooter'
import CustomScheduleFormHeader from './CustomScheduleFormHeader'
import TelecommandForm from './TelecommandForm'
import './CustomScheduleModal.scss'
import { CircularProgress } from '@mui/material'
import { getInitialFormTelecommandData, maxScheduleDuration, validateCustomScheduleData, validateCustomScheduleFormData } from '../../../../../utils'
import { Command, ScheduleTask } from '../../../../../types'
import EmptyPage from '../../../../../custom_components/EmptyPage'
import useConsole from '../../../../../hooks/useConsole'

const CustomScheduleModal = () => {
    const [isDateTimeValid, setIsDateTimeValid] = useState(true);

    const {
        addNewTask,
        onMenuClick,
        onTaskSelect,
        onSaveSchedule,
        taskPlanDetails,
        onCommandSelect,
        generateSchedule,
        addUpdateNewTelecommand,
        customScheduleModalData,
        setCustomScheduleModalData,
        setScheduleConfirmationModal,
    } = useCustomSchedule()

    const scheduleGenerated = useMemo(() => customScheduleModalData?.scheduleGenerated || false, [customScheduleModalData?.scheduleGenerated])
    const selectedTask = useMemo(() => customScheduleModalData?.selectedTask, [customScheduleModalData?.selectedTask])
    const scheduledTasks = useMemo(() => customScheduleModalData?.scheduledTasks, [customScheduleModalData?.scheduledTasks])
    const mode = useMemo(() => customScheduleModalData?.mode || 'create', [customScheduleModalData?.mode])
    const scheduleType = useMemo(() => customScheduleModalData?.scheduleType, [customScheduleModalData?.scheduleType])
    const open = useMemo(() => customScheduleModalData?.mode && scheduleType && ['Generate', 'Build'].includes(scheduleType), [customScheduleModalData?.mode, scheduleType])
    const formData = useMemo(() => customScheduleModalData?.formData, [customScheduleModalData?.formData])
    const formType = useMemo(() => customScheduleModalData?.formType ?? 'schedule', [customScheduleModalData?.formType])

    const isTaskForm = useMemo(() => ['add-task'].includes(formType), [formType])
    const isCommandForm = useMemo(() => ['add-telecommand', 'update-telecommand'].includes(formType), [formType])

    const formLoading = useMemo(() => customScheduleModalData?.formLoading || false, [customScheduleModalData?.formLoading])
    const loading = useMemo(() => customScheduleModalData?.loading || false, [customScheduleModalData?.loading])

    const disabled = useMemo(() => loading || formLoading || false, [loading, formLoading])
    const sourceId = useMemo(() => formData?.sourceId, [formData?.sourceId])
    const taskData = useMemo(() => formData?.taskData, [formData?.taskData])
    const disabledCancelButton = useMemo(() => loading || formLoading || (scheduleType === 'Build' && !scheduledTasks?.length) || false, [loading, formLoading, scheduleType, scheduledTasks])
    const duration = useMemo(() => customScheduleModalData?.duration || null, [customScheduleModalData?.duration])
    const selectedSchedule = useMemo(() => customScheduleModalData?.selectedSchedule, [customScheduleModalData?.selectedSchedule?.scheduledTasks])
    const selectedTelecommand = useMemo(() => customScheduleModalData?.selectedTelecommand, [customScheduleModalData?.selectedTelecommand])
    const recentlyEditedTelecommand = useMemo(() => customScheduleModalData?.recentlyEditedTelecommand ?? null, [customScheduleModalData?.recentlyEditedTelecommand])
    const isFirstCommandSelected = useMemo(() => !selectedTask?.commandsList?.length, [selectedTask?.commandsList])
    const isFirstTask = useMemo(() => !scheduledTasks?.length, [scheduledTasks])
    const disabledSpecification = useMemo(() => !scheduledTasks?.length || (!selectedTask?.commandsList?.length && formType === 'add-telecommand') || (formType === 'update-telecommand'), [scheduledTasks, selectedTask, formType])
    const remainingDuration = useMemo(() => Number(duration || maxScheduleDuration) * 60, [duration])
    const { busCommandOptions } = useConsole();


    const showEmptyForm = useMemo(() => formType === 'empty-page', [formType])

    useEffect(() => {
        if (!recentlyEditedTelecommand) return

        const editedAt = recentlyEditedTelecommand.updatedAt
        const timer = window.setTimeout(() => {
            setCustomScheduleModalData((prev) => {
                if (!prev || prev.recentlyEditedTelecommand?.updatedAt !== editedAt) return prev
                return {
                    ...prev,
                    recentlyEditedTelecommand: null
                }
            })
        }, 2200)

        return () => window.clearTimeout(timer)
    }, [recentlyEditedTelecommand, setCustomScheduleModalData])

    const primaryButtonText = useMemo(() => {
        if (isCommandForm) {
            return formLoading ? formType === 'update-telecommand' ? 'Updating' : 'Adding' : formType === 'update-telecommand' ? 'Update' : 'Add Telecommand'
        } else if (isTaskForm) {
            return formLoading ? 'Adding Task' : formType === 'add-task' ? 'Add Task' : 'Update Task'
        }
        return formLoading ? 'Generating' : (scheduledTasks && scheduledTasks?.length > 0) ? 'Re-Generate Schedule' : 'Generate Schedule'
    }, [isCommandForm, isTaskForm, formType, scheduledTasks, formLoading])

    const title = useMemo(() => {
        if (isCommandForm) {
            return formType === 'update-telecommand' ? 'Edit Telecommand' : 'Add Telecommand'
        }
        else if (isTaskForm) {
            return formType === 'add-task' ? 'Add Task' : 'Update Task'
        }
        else if (scheduleType === 'Generate' && formType === 'schedule') {
            return mode === 'update' ? 'Update Schedule' : 'Generate Schedule'
        }
        else if (scheduleType === 'Build') {
            return 'Build Schedule'
        }
        return mode === 'update' ? 'Update Schedule' : 'Generate Schedule'
    }, [scheduleType, mode, formType, isCommandForm, isTaskForm])

    const onExit = () => {
        setScheduleConfirmationModal({
            open: true,
            mode: 'cancel',
            message: scheduleGenerated ? 'Discard Schedule Creation?' : (scheduleType === 'Build' ? 'Exit Schedule Build?' : 'Exit Schedule Generation?'),
            description: scheduleGenerated ? 'Confirm to discard creation. Once confirmed, this schedule will be discarded.' : 'Confirm to exit creation mode. Any unsaved changes will be lost.',
            view: 'editable',
        })
    }

    const onCancel = () => {
        setCustomScheduleModalData((prev) => {
            return {
                ...prev!,
                formType: scheduleType === 'Generate' ? 'schedule' : 'empty-page',
                formData: {
                    ...prev?.formData!,
                    taskData: {
                        ...prev?.formData?.taskData!,
                        taskName: '',
                        adhocTaskId: '',
                        taskSource: 'adhocTask',
                    },
                    telecommandData: getInitialFormTelecommandData()
                },
                selectedTelecommand: null,
            }
        })
    }

    const onAddTask = (task: ScheduleTask) => {
        setCustomScheduleModalData((prev) => ({
            ...prev!,
            formType: 'add-task',
            selectedTask: task,
            formData: {
                ...prev?.formData!,
                taskData: {
                    ...prev?.formData?.taskData!,
                    taskName: '',
                    adhocTaskId: '',
                    taskSource: 'adhocTask',
                    time: Math.round((task?.startTime ?? 0) + 10),
                    delay: 10,
                    timeOption: 'startTime',
                    taskStartTime: task?.startTime,
                    taskEndTime: task?.endTime
                }
            },
            selectedTelecommand: null,
            scheduleGenerated: false
        }))
    }

    const onAddCommand = (task: ScheduleTask, command: Command | null) => {
        setCustomScheduleModalData((prev) => ({
            ...prev!,
            formType: 'add-telecommand',
            formData: {
                ...prev?.formData!,
                telecommandData: {
                    ...prev?.formData?.telecommandData!,
                    type: "Bus",
                    name: "",
                    time: command ? command?.epoch + command?.duration + 10 : task?.startTime,
                    component: "All",
                    sequenceData: null,
                    duration: 2,
                    delay: command ? 10 : 0,
                    timeOption: 'startTime',
                    commandStartTime: command ? command?.epoch : null,
                    commandEndTime: command ? command?.epoch + command?.duration : null,
                    taskId: task?.taskId ?? command?.taskId ?? '',
                    commandId: command?.commandId ?? ''
                }
            },
            selectedTask: task,
            selectedTelecommand: command,
            scheduleGenerated: false
        }))
    }

    const getComponentValue = (commandName: string) => {
        const command: any = busCommandOptions.find((item: any) => item?.value?.name === commandName)
        return command?.value?.funcCategory
    }

    const onFormSubmit = () => {
        const validate = validateCustomScheduleFormData(formType, scheduleType, formData, isFirstTask, isFirstCommandSelected)
        if (!validate) return
        setCustomScheduleModalData((prev) => ({
            ...prev!,
            formLoading: true
        }))
        if (scheduleType === 'Generate' && formType === 'schedule') {
            const data = {
                duration: Number(formData?.duration),
                source: formData?.source,
                sourceId: formData?.sourceId,
                startTime: formData?.startTime
            }
            generateSchedule(data)
        } else if (isTaskForm) {
            const data = {
                adhocTaskId: formData?.taskData?.adhocTaskId,
                source: formData?.taskData?.taskSource,
                startTime: formData?.taskData?.time ? Math.floor(formData?.taskData?.time) : formData?.taskData?.time,
                taskName: formData?.taskData?.taskName,
            }
            addNewTask(data)
        } else if (isCommandForm) {
            const commandData = formData?.telecommandData
            const data = {
                component: getComponentValue(commandData?.name ?? '') || commandData?.component,
                duration: Number(commandData?.duration),
                name: commandData?.name,
                sequenceData: commandData?.sequenceData,
                startTime: Math.round(Number(commandData?.time)),
                type: commandData?.type,
            }

            const taskId = commandData?.taskId ?? ''
            const commandId = formType === 'update-telecommand' ? formData?.telecommandData?.commandId : ''
            addUpdateNewTelecommand(formType, data, taskId, commandId)
        }
    }

    const onScheduleSubmit = () => {
        const data = {
            ...customScheduleModalData?.selectedSchedule,
            scheduleName: customScheduleModalData?.name?.trim(),
        }
        const validate = validateCustomScheduleData(data)
        if (!validate) return
        setCustomScheduleModalData((prev) => ({
            ...prev!,
            loading: true
        }))
        onSaveSchedule(mode, data)
    }

    if (!open) return null
    return (
        <div className='custom__schedule__modal__container'>
            <div className={`custom__schedule__modal__container__body ${disabled ? 'disabled' : ''}`}>
                <div className='custom__schedule__modal__container__body__left__section'>
                    <div className={`custom__schedule__modal__form__container 
                        ${formType === 'update-telecommand' ? 'telecommand__form' : ''} 
                        ${disabled ? 'disabled' : ''} ${formType}`}
                    >
                        <CustomScheduleFormHeader title={title} />
                        {!showEmptyForm &&
                            <Fragment>
                                {isCommandForm ? (
                                    <TelecommandForm
                                        formType={formType}
                                        disabledSpecification={disabledSpecification}
                                        onChange={(value: any) => {
                                            setCustomScheduleModalData((prev) => ({
                                                ...prev!,
                                                formData: { ...prev?.formData, ...value }
                                            }))
                                        }}
                                        telecommandData={formData?.telecommandData || (getInitialFormTelecommandData() as any)}
                                        remainingDuration={remainingDuration}
                                        onDateTimeValidationChange={setIsDateTimeValid}
                                    />
                                ) : (
                                    <CustomScheduleFormBody
                                        taskPlanDetails={taskPlanDetails}
                                        formType={formType}
                                        formData={formData}
                                        remainingDuration={remainingDuration}
                                        disabledSpecification={disabledSpecification}
                                        onDateTimeValidationChange={setIsDateTimeValid}
                                        onChange={(value) => {
                                        setCustomScheduleModalData((prev) => {
                                            const newFormData = { ...prev?.formData, ...value }
                                            const snapshot = prev?.generatedFormSnapshot
                                            const isScheduleFormUnchanged = formType === 'schedule' && !!snapshot &&
                                                String(snapshot.source) === String(newFormData.source) &&
                                                String(snapshot.sourceId) === String(newFormData.sourceId) &&
                                                Number(snapshot.startTime) === Number(newFormData.startTime) &&
                                                Number(snapshot.duration) === Number(newFormData.duration)
                                            return {
                                                ...prev!,
                                                formData: newFormData,
                                                scheduleGenerated: isScheduleFormUnchanged
                                            }
                                        })
                                    }}
                                        customScheduleModalData={customScheduleModalData}
                                    />
                                )}
                                <CustomScheduleFormFooter
                                    formType={formType}
                                    disabledCancel={disabledCancelButton}
                                    disabled={disabled || scheduleGenerated || (formType === 'schedule' && !sourceId) || (formType === 'add-task' && !taskData?.adhocTaskId && taskData?.taskSource !== 'customTask') || (taskData?.taskSource === 'customTask' && (!taskData?.taskName || !taskData?.time)) || !isDateTimeValid}
                                    onCancel={onCancel}
                                    onFormSubmit={onFormSubmit}
                                    secondaryButtonText={"Cancel"}
                                    primaryButtonText={primaryButtonText}
                                    loading={formLoading}
                                />
                            </Fragment>
                        }
                        {showEmptyForm && (
                            <EmptyPage
                                classNames='empty__form'
                                icon={<NoCommandSelectedIcon />}
                                content='No Telecommand Selected'
                                subContent='Select a telecommand to review or modify its configuration, or create a new task to proceed.'
                            />
                        )}
                    </div>
                </div>
                <div className='custom__schedule__modal__container__body__right__section'>
                    <RightSection
                        message={formLoading ? (isTaskForm ? 'ADDING TASK...' : isCommandForm ? 'ADDING TELECOMMAND...' : scheduleType === 'Build' ? 'BUILDING SCHEDULE...' : 'GENERATING SCHEDULE...') : (scheduleType === 'Build' ? 'No Tasks Added Yet' : 'No Schedule to Preview')}
                        subMessage={formLoading ? (isTaskForm ? 'Task will be visible once added to the schedule' : isCommandForm ? 'Telecommand will be visible once added to the task' : scheduleType === 'Build' ? 'Schedule will be visible once tasks are added' : 'Schedule will be visible once a schedule is generated') : (scheduleType === 'Build' ? 'Add a task to start building your schedule' : 'Please generate a schedule to preview')}
                        loading={formLoading}
                        showAddButton={customScheduleModalData?.formType}
                        icon={formLoading ? <GeneratingScheduleIcon /> : <NoSchedulePreview />}
                        isReadonly={false}
                        selectedSchedule={selectedSchedule ? selectedSchedule : null}
                        selectedTask={customScheduleModalData?.selectedTask ?? null}
                        selectedTelecommand={customScheduleModalData?.selectedTelecommand ?? null}
                        recentlyEditedTelecommand={recentlyEditedTelecommand}
                        name={customScheduleModalData?.name || ''}
                        startTime={customScheduleModalData?.startTime || null}
                        duration={Number(duration) || null}
                        onMenuClick={onMenuClick}
                        onTaskSelect={onTaskSelect}
                        onCommandSelect={onCommandSelect}
                        onAddTask={onAddTask}
                        onAddCommand={onAddCommand}
                        scheduleType={scheduleType}
                        onChangeName={(value) => {
                            setCustomScheduleModalData((prev) => ({
                                ...prev!,
                                name: value
                            }))
                        }}
                    />
                </div>
            </div>
            <div className='custom__schedule__modal__container__footer'>
                <button className='modal__footer__button button__outline'
                    onClick={onExit}
                >{scheduleGenerated ? 'Discard' : 'Exit'}</button>
                <button className='modal__footer__button button__primary' disabled={disabled}
                    onClick={onScheduleSubmit}
                >
                    {loading ? <Fragment> Saving&nbsp; <CircularProgress size={16} color="inherit" /></Fragment> : 'Save Schedule'}
                </button>
            </div>
        </div>
    )
}

export default CustomScheduleModal
